import { useAuth } from '@/auth/useAuth';
import { type Role, ROLES } from '@/constants/roles';
import { ROUTES } from '@/constants/routes';
import { useWorkspace } from '@/features/company-portal/workspace/WorkspaceProvider';

export interface DashboardTarget {
  path: string;
  label: string;
}

/**
 * Staff dashboards resolved from platform roles. Candidate/Company are NOT here: those are
 * workspaces, resolved from membership state below.
 *
 * <p>All three platform tiers appear, in ladder order (ADR-0032) — key order is load-bearing, since
 * the lookup below takes the first match and a user holding a stale second tier must resolve to the
 * more privileged one. Recruiter and Vendor pointed at /recruiter and /vendor, which were placeholder
 * shells and have been removed — leaving them here would put a "Back to Dashboard" link and an
 * account-menu entry on a route that no longer resolves. Recruiting is reached through company
 * membership, below.</p>
 *
 * <p>Support lands on the user directory rather than the dashboard: the dashboard is built from
 * company, candidate, job and application aggregates, and Support is authorized for almost none of
 * them, so it would greet them with a page of em-dashes. The directory is the surface their work
 * actually starts from.</p>
 */
const STAFF_DASHBOARDS: Partial<Record<Role, DashboardTarget>> = {
  [ROLES.Admin]: { path: ROUTES.admin, label: 'Admin Dashboard' },
  [ROLES.PlatformAdmin]: { path: ROUTES.admin, label: 'Admin Dashboard' },
  [ROLES.Support]: { path: `${ROUTES.admin}/users`, label: 'Platform Users' },
};

/**
 * The current user's "home" — used by the "Back to Dashboard" link on account/identity pages.
 * Staff roles win; otherwise the business workspace: Company access (MEMBERSHIP-derived — a
 * member-only account holds no platform role at all) before Candidate, honoring the remembered
 * workspace for dual-persona accounts. An account with neither workspace goes to /welcome — every
 * target this returns must be one the user can actually open, since the link renders regardless.
 */
export function useDashboardTarget(): DashboardTarget {
  const { user } = useAuth();
  const workspace = useWorkspace();

  const staff = (Object.keys(STAFF_DASHBOARDS) as Role[]).find((role) => user?.roles.includes(role));
  if (staff) return STAFF_DASHBOARDS[staff]!;

  const candidateTarget = { path: ROUTES.candidate, label: 'Candidate Dashboard' };
  const companyTarget = {
    path: ROUTES.company,
    label: workspace.activeMembership
      ? `${workspace.activeMembership.companyName} Workspace`
      : 'Company Workspace',
  };

  if (workspace.hasCompanyAccess && workspace.hasCandidateWorkspace) {
    return workspace.lastWorkspace?.kind === 'candidate' ? candidateTarget : companyTarget;
  }
  if (workspace.hasCompanyAccess) return companyTarget;
  if (workspace.hasCandidateWorkspace) return candidateTarget;

  // Neither workspace: an account that skipped onboarding holds no Candidate role at all, and
  // `/candidate` is gated on exactly that role (`RoleRoute`), so returning the candidate target
  // here pointed "Back to Candidate Dashboard" straight at a 403. `hasCandidateWorkspace` IS that
  // role check (WorkspaceProvider), which is why gating on it is the whole fix.
  //
  // /welcome is the right home for this state on precedent, not invention: WorkspaceChooserPage
  // already resolves "neither workspace" to it. It carries no role guard, and it cannot bounce
  // back — every OnboardingPage redirect requires a workspace this user does not have.
  return { path: ROUTES.welcome, label: 'Welcome' };
}

/**
 * Every destination the account can call "home": staff dashboards from roles, the Candidate
 * workspace, and one entry PER active company membership (labelled with the company's name —
 * never a generic "Company Dashboard"). Used by the account menu's workspace section.
 */
export interface WorkspaceMenuTarget extends DashboardTarget {
  /** Set for company entries — clicking must also activate that company in the workspace. */
  companyId?: string;
}

export function useDashboardTargets(): WorkspaceMenuTarget[] {
  const { user } = useAuth();
  const workspace = useWorkspace();

  const targets: WorkspaceMenuTarget[] = (Object.keys(STAFF_DASHBOARDS) as Role[])
    .filter((role) => user?.roles.includes(role))
    .map((role) => STAFF_DASHBOARDS[role]!);

  if (workspace.hasCandidateWorkspace) {
    targets.push({ path: ROUTES.candidate, label: 'Candidate Workspace' });
  }

  for (const membership of workspace.activeMemberships) {
    targets.push({
      path: ROUTES.company,
      label: membership.companyName,
      companyId: membership.companyId,
    });
  }

  // A founder mid-registration has no membership yet but still owns the company journey. Gated on
  // hasCompanyAccess, not on the registration alone: a Completed registration survives its company's
  // deletion as an audit record, and without this the account menu kept offering a workspace that no
  // longer exists.
  if (workspace.activeMemberships.length === 0 && workspace.hasCompanyAccess && workspace.registration) {
    targets.push({ path: ROUTES.company, label: workspace.registration.companyName });
  }

  return targets;
}
